<!--
  Avisa al usuario que alguno de los medias que quiere subir excede la resolución soportada por el hardware de
  reproducción, y le ofrece contratar el Conversor incorporado (si algún plan de la empresa lo permite) u optimizar
  el archivo por su cuenta. Lo abre `mediaHardwareInspector.service.ts` -> inspectMediaHardware.
-->
<script lang="ts" setup>
import { computed } from 'vue';
import PolyButton from '@/components/poly/PolyButton.vue';
import type { InspectedMedia, MediaHardwareFeedbackResult } from '@/interfaces/mediaHardwareInspector.interfaces';

const props = defineProps<{
  resolve: (value: MediaHardwareFeedbackResult) => void
  medias: InspectedMedia[]
  recommendedResolutionLabel: string
  canHireConversor: boolean
  converterPrice: string
  totemsCount: number
  converterTotalPrice: number
}>()

const externalTools = ['https://handbrake.fr/']

// Determina si un media corresponde a un video, soportando tanto el mediaType del modelo como el mime type de un File del navegador
function isVideoMedia(media: InspectedMedia): boolean {
  return media.mediaType === 'VIDEO' || (typeof media.type === 'string' && media.type.startsWith('video'))
}

// Arma el texto de resolución del media, o undefined si no está disponible
function resolutionLabel(media: InspectedMedia): string | undefined {
  if (!media.width || !media.height) return undefined
  return `${window.i18next.t('Resolución')}: ${media.width}x${media.height}`
}

// Título del popup: si es un solo media respeta el género de "video"/"imagen", si son varios usa un título neutro
const title = computed(() => {
  if (props.medias.length > 1) return window.i18next.t('Los archivos que deseas cargar no son óptimos para su reproducción en el player:')
  return isVideoMedia(props.medias[0])
    ? window.i18next.t('El video que deseas cargar no es óptimo para su reproducción en el player:')
    : window.i18next.t('La imagen que deseas cargar no es óptima para su reproducción en el player:')
})

function accept() {
  props.resolve({ accepted: true })
}

// Se usa cuando ningún plan de la empresa permite contratar el conversor: no ofrece el servicio, solo informa
function acceptWithoutConversorOffer() {
  props.resolve({ conversorNotAvailable: true })
}

function cancel() {
  props.resolve(false)
}
</script>
<template lang="pug">
.mediaHardwareFeedback.pmx-col
  i.fa-regular.fa-triangle-exclamation.warning-icon
  strong.title {{ title }}
  .pmx-col.file-info-box(v-if="medias.length")
    template(v-for="(media, index) in medias" :key="index")
      .pmx-col
        span(v-if="media.name") {{ media.name }}
        span(v-if="resolutionLabel(media)") {{ resolutionLabel(media) }}
      hr.media-separator(v-if="index < medias.length - 1")
  span
    | {{ $t('Te recomendamos no superar una resolución de') }} {{ recommendedResolutionLabel }} px {{ $t('para evitar fallas en la reproducción.') }}
  span
    | {{ $t('Puedes utilizar herramientas de conversión externas como') }}
    template(v-for="(tool, index) in externalTools" :key="tool")
      | &nbsp;
      a.external-tool-link(:href="tool" target="_blank" rel="noopener" :data-testid="`media-hardware-feedback-herramienta-externa-link-${index}`") {{ tool }}
      | {{ index === externalTools.length - 1 ? "" : ", " }}
    span(v-if="canHireConversor")
      | {{ $t('o contratar nuestro') }}
      strong &nbsp;{{ $t('Conversor incorporado por') }} {{ converterPrice }} {{ $t('USD mensuales por pantalla.') }}
  strong(v-if="canHireConversor && totemsCount")
    | {{ $t('En tu caso, por') }} {{ totemsCount }} {{ $t(totemsCount === 1 ? 'pantalla' : 'pantallas') }}{{ $t(', el valor total sería de') }} {{ converterTotalPrice }} {{ $t('USD mensuales.') }} {{ $t('¿Deseas activarlo?') }}
  strong(v-if="canHireConversor && !totemsCount")
    | {{ $t('¿Deseas activarlo?') }}
  .pmx-row.actions(v-if="canHireConversor")
    PolyButton(button-style="Outline" @click="cancel" data-testid="media-hardware-feedback-cancelar-boton") {{ $t('NO, CANCELAR') }}
    PolyButton(@click="accept" data-testid="media-hardware-feedback-activar-boton") {{ $t('SI, ACTIVAR') }}
  .pmx-row.actions(v-if="canHireConversor === false")
    PolyButton(@click="acceptWithoutConversorOffer" data-testid="media-hardware-feedback-aceptar-sin-conversor-boton") {{ $t('ACEPTAR') }}
</template>
<style lang="less" scoped>
@import '../frontend/mobile/themevars.less';

.mediaHardwareFeedback {
  background-color: white;
  align-items: center;
  text-align: center;
  gap: 1em;
  padding: 1.5em 2em;
  max-width: 36.25em;
  max-height: 85vh;
  overflow-y: auto;
  box-sizing: border-box;
  border-radius: 0.923em;

  .warning-icon {
    font-size: 5.25em;
    color: @feedbackYellow;
    flex-shrink: 0;
  }

  .title {
    font-size: 1.05rem;
    flex-shrink: 0;
  }

  .file-info-box {
    background-color: @gray100;
    border: 0.0625em solid @gray30;
    border-radius: 0.5em;
    padding: 0.75em 1em;
    width: 100%;
    text-align: left;
    gap: 0.5em;
    max-height: 6em;
    overflow-y: auto;
    flex-shrink: 0;

    > div {
      gap: 0.25em;
      flex-shrink: 0;
    }
  }

  .actions {
    gap: 1.25em;
    width: 100%;
    justify-content: center;
    flex-shrink: 0;
  }

  .media-separator {
    border: 0;
    border-top: 1px solid @gray35;
    margin: 10px 0;
  }

  .external-tool-link {
    color: @primaryColor;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: @primaryDark;
    }
  }
}
</style>
